// Final district pass. Coordinates + IDs prevent reused east IDs leaking to Okubo.
import type { HdBuilding } from '../../common/src/data';
import type { LightFn } from './hd-registry';
import { type G, K, HT, px, frame, alpha, lt } from './hd-kit';
import { panel, fan, window } from './hd-hachi-study';
type Style = 'tower' | 'station' | 'retail' | 'hotel' | 'atrium' | 'service' | 'deck' | 'club';
type Unit = readonly [number, number, number, number];
type Spec = readonly [string, number, number, Style, Unit | null];
export const NISHI_REFRESH: readonly Spec[] = [
  ["w-a", 0, 101, "tower", [18, 276, 40, 24]],
  ["w-b", 0, 115, "service", [18, 308, 40, 24]],
  ["w-c", 0, 129, "tower", [18, 308, 40, 24]],
  ["hall", 13, 101, "station", null],
  ["metro-tw", 47, 101, "tower", [218, 52, 40, 24]],
  ["arc-tw", 64, 101, "tower", null],
  ["stn-b", 83, 101, "station", [250, 52, 40, 24]],
  ["mall", 98, 101, "retail", [18, 28, 112, 48]],
  ["e-tower", 123, 101, "tower", null],
  ["hq-a", 13, 118, "tower", [122, 156, 112, 48]],
  ["hq-b", 25, 118, "tower", [18, 156, 112, 48]],
  ["off-a", 83, 118, "tower", [154, 188, 112, 48]],
  ["off-b", 101, 118, "tower", [170, 188, 112, 48]],
  ["e-off", 123, 118, "tower", null],
  ["atr-a", 47, 125, "atrium", [122, 20, 40, 24]],
  ["atr-b", 60, 125, "retail", [154, 20, 40, 24]],
  ["pod-1", 3, 141, "service", null],
  ["pod-2", 23, 141, "service", null],
  ["pod-3", 43, 141, "service", null],
  ["pod-4", 63, 141, "service", null],
  ["pod-5", 83, 141, "service", null],
  ["pod-6", 103, 141, "service", null],
  ["n1", 128, 101, "tower", [18, 84, 40, 24]],
  ["n2", 136, 101, "hotel", [18, 72, 72, 36]],
  ["n3", 143, 101, "deck", [18, 84, 40, 24]],
  ["n4", 150, 101, "retail", [18, 72, 72, 36]],
  ["n5", 167, 101, "tower", [154, 72, 72, 36]],
  ["n6", 183, 101, "hotel", [18, 84, 40, 24]],
  ["n7", 207, 101, "tower", [122, 84, 40, 24]],
  ["n8", 216, 101, "retail", [138, 84, 40, 24]],
  ["n9", 224, 101, "deck", [18, 84, 40, 24]],
  ["n10", 232, 101, "hotel", [18, 84, 40, 24]],
  ["e1", 251, 101, "tower", [18, 188, 112, 48]],
  ["e2", 251, 112, "deck", [18, 104, 72, 36]],
  ["e3", 251, 123, "tower", [18, 92, 112, 48]],
  ["e4", 251, 134, "service", [18, 180, 40, 24]],
  ["s1", 128, 118, "tower", [138, 188, 112, 48]],
  ["s2", 141, 118, "hotel", [18, 188, 112, 48]],
  ["s3", 167, 118, "tower", [18, 188, 112, 48]],
  ["s5", 190, 118, "retail", [18, 188, 112, 48]],
  ["c1", 207, 118, "club", null],
  ["c2", 214, 118, "club", null],
  ["c3", 225, 118, "club", null],
  ["c4", 207, 130, "club", null],
  ["c5", 215, 130, "club", null],
  ["c6", 225, 130, "club", null],
  ["c7", 230, 130, "club", null],
  ["c8", 207, 119, "club", null],
  ["c9", 207, 124, "club", null],
  ["c10", 240, 119, "club", null],
  ["w1", 128, 141, "service", null],
  ["w2", 159, 141, "service", null],
  ["w3", 190, 141, "service", null],
  ["w4", 221, 141, "service", null],
];
const SCOPE = new Map(NISHI_REFRESH.map(s => [`${s[1]}:${s[2]}:${s[0]}`, s]));
function specOf(b: HdBuilding) { return SCOPE.get(`${b.x}:${b.y}:${b.id}`); }

/** Below original equipment: roof structure and service machinery retain the existing silhouette. */
export function paintNishiRoof(g: G, b: HdBuilding, x: number, y: number) {
  const s = specOf(b); if (!s || s[3] === 'club') return false;
  const w = b.w * HT, rh = (b.h - b.face) * HT, style = s[3];
  g.save(); g.beginPath(); g.rect(x, y, w, rh); g.clip();
  // Raised aluminum coping, separated from the original outer parapet by a dark drainage slot.
  frame(g, x + 7, y + 7, w - 14, rh - 14, K.ink, 2);
  px(g, x + 9, y + 9, w - 18, 2, K.metalHi);
  px(g, x + 9, y + 11, 2, rh - 20, K.metal);
  for (let xx = 22; xx < w - 12; xx += 28) px(g, x + xx, y + 9, 2, 2, K.ink);
  if (rh >= 64) {
    // Stainless maintenance track; subtle transverse seams make tall slabs feel constructed.
    px(g, x + w - 18, y + 18, 7, rh - 32, K.ink);
    for (let yy = 22; yy < rh - 15; yy += 9) px(g, x + w - 17, y + yy, 5, 2, K.metalLo);
    for (let yy = 58; yy < rh - 24; yy += 56) {
      px(g, x + 16, y + yy, w - 42, 1, alpha(K.metalHi, .24));
      px(g, x + 16, y + yy + 1, w - 42, 1, alpha(K.night, .3));
    }
    if (w > 110) {
      panel(g, x + 17, y + rh - 18, w - 40, 8, K.metalLo);
      for (let xx = 22; xx < w - 27; xx += 10) px(g, x + xx, y + rh - 16, 2, 4, K.ink);
    }
  } else {
    // The station hall and border podiums have only one roof tile: low glass/metal fins fit it.
    for (let xx = 20; xx < w - 25; xx += 32) {
      px(g, x + xx, y + 17, 22, 5, style === 'station' ? K.glass : K.metalLo);
      px(g, x + xx, y + 17, 22, 1, K.metalHi);
    }
  }
  const u = s[4];
  if (u) {
    const [ux, uy, uw, uh] = u, xx = x + ux, yy = y + uy;
    px(g, xx + 4, yy + 5, uw, uh, alpha(K.night, .38));
    if (style === 'tower' || style === 'service' || style === 'deck') {
      panel(g, xx, yy, uw, uh, K.metalLo);
      if (uw >= 72 && uh >= 36) {
        fan(g, xx + 5, yy + 4); fan(g, xx + 37, yy + 4);
      }
      for (let j = 5; j < uh - 4; j += 5) px(g, xx + uw - 16, yy + j, 11, 1, K.metalHi);
      px(g, xx + 4, yy + uh - 5, uw - 8, 2, K.metal);
    } else {
      window(g, xx, yy, uw, uh, K.glassLo);
      for (let j = 16; j < uw - 4; j += 16) px(g, xx + j, yy + 3, 2, uh - 6, K.metalHi);
      px(g, xx + 4, yy + Math.floor(uh / 2), uw - 8, 2, K.metalLo);
    }
  }
  g.restore(); return true;
}

function tube(g: G, x: number, y: number, w: number, h: number, c: string) {
  px(g, x - 3, y - 3, w + 6, h + 6, alpha(c, .1));
  px(g, x - 1, y - 1, w + 2, h + 2, alpha(c, .25));
  px(g, x, y, w, h, c);
  px(g, x, y, w > h ? w : 1, w > h ? 1 : h, lt(c, .55));
}

/** Architectural accents around original curtain walls, signs and working entrances. */
export function paintNishiFront(g: G, b: HdBuilding, x: number, y: number, L: LightFn) {
  const s = specOf(b); if (!s || !b.face || s[3] === 'club') return;
  const w = b.w * HT, h = b.face * HT, fy = y + (b.h - b.face) * HT, bottom = fy + h, style = s[3];
  g.save(); g.beginPath(); g.rect(x, fy, w, h); g.clip();
  for (const xx of [x + 2, x + w - 6]) {
    px(g, xx, fy + 4, 4, h - 8, K.metalLo);
    px(g, xx, fy + 4, 1, h - 8, K.metalHi);
  }
  if (style === 'tower') {
    // Vertical pink accent fins distinguish the skyline while preserving the cool glass grid.
    for (const xx of [x + 6, x + w - 8]) {
      tube(g, xx, fy + 10, 2, h - 23, K.pink);
      L(xx - 1, fy + 16, 4, h - 34, K.pink, .18, false);
    }
    px(g, x + 10, fy + 4, w - 20, 2, K.metalHi);
    // Warm lobby strips flank the original cyan entrance: neither doors nor signs move.
    const pw = Math.min(45, Math.floor(w * .2));
    for (const xx of [x + 10, x + w - 10 - pw]) {
      tube(g, xx, bottom - 7, pw, 1, K.amber);
      L(xx, bottom - 13, pw, 7, K.amber, .24, true);
    }
  } else if (style === 'hotel' || style === 'retail') {
    px(g, x + 5, fy + 5, w - 10, h - 10, alpha(K.pink, .07));
    tube(g, x + 9, fy + 3, w - 18, 2, K.pink);
    for (const xx of [x + 6, x + w - 8]) tube(g, xx, fy + 21, 2, h - 30, K.pink);
    const pw = Math.min(72, Math.floor(w * .26));
    for (const xx of [x + 12, x + w - 12 - pw]) {
      tube(g, xx, bottom - 7, pw, 2, K.pink);
      L(xx, bottom - 15, pw, 10, K.pink, .44, true);
    }
    L(x + 12, fy + 8, w - 24, 14, K.pink, .26, false);
  } else if (style === 'station' || style === 'atrium') {
    // Existing cyan transit signs remain the focal point, with warm entrance pools.
    const cy = bottom - 49;
    px(g, x + 8, cy, w - 16, 5, K.ink);
    px(g, x + 9, cy + 1, w - 18, 2, K.metalHi);
    tube(g, x + 10, cy + 4, w - 20, 1, K.amber);
    for (let xx = 22; xx < w - 22; xx += 76) {
      const pw = Math.min(45, w - xx - 12);
      L(x + xx, bottom - 14, pw, 8, K.amber, .24, true);
    }
  } else {
    // Loading bays and parking remain utilitarian. Recessed lamps and slab edges, no nightclub frame.
    px(g, x + 6, fy + 5, w - 12, 2, K.concreteHi);
    for (let xx = 12; xx < w - 24; xx += 110) {
      panel(g, x + xx, fy + 9, 18, 7, K.metalLo);
      px(g, x + xx + 3, fy + 11, 12, 2, K.warmHi);
      L(x + xx + 2, fy + 10, 14, 5, K.amber, .2, false);
    }
  }
  g.restore();
}

/** Called inside paintClub's existing solid-wall loop, before its mask/sign overlays.
 * These fittings never bridge a door gap or paint across the walkable dance floor. */
export function paintNishiClubWall(g: G, x: number, y: number, north: boolean, south: boolean) {
  // Beveled cap with a bolted metal seam; the horizontal ring is continuous but every panel is legible.
  px(g, x + 2, y + 1, 28, 1, K.concreteHi);
  for (const dx of [4, 26]) { px(g, x + dx, y + 2, 2, 2, K.ink); px(g, x + dx, y + 2, 1, 1, K.metalHi); }
  if (north || south) {
    px(g, x + 1, y + 7, 2, 21, K.metalLo);
    px(g, x + 29, y + 7, 2, 21, K.metalHi);
    px(g, x + 4, y + 29, 24, 1, alpha(K.pink, .5));
  } else {
    for (const dx of [6, 23]) {
      panel(g, x + dx, y + 9, 4, 14, K.metalLo);
      px(g, x + dx + 1, y + 12, 2, 5, K.amber);
    }
  }
}
